import { useAuth } from '@/hooks'
import { useTransmit } from '@/hooks/useTransmit'
import { invalidarCacheDoModulo } from '@/service/api/cache-updates'
import {
    campanhas_ciencia,
    notificacoes_api,
    NotificationMessage,
    NotificationSilentMessage,
    NotificationVisualMessage,
} from '@/service/api/endpoints/notificacoes'
import tokenRefresher from '@/service/api/token-refresher'
import { StoredNotification } from '@/templates/Notificacoes/Central'
import { modoCiencia } from '@/templates/Notificacoes/ciencia'
import { ModalCampanha } from '@/templates/Notificacoes/ModalCampanha'
import { ExternalLinkIcon } from 'lucide-react'
import React, { createContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface NotificacoesContextProps {
    notifications: (NotificationMessage & { seen: boolean })[]
    silentNotifications: StoredNotification[]
    markAsSeen: (id: string | '*') => void
    isReady: boolean
}

type Props = {
    children: React.ReactNode
}

export const NotificacoesContext = createContext(
    {} as NotificacoesContextProps | undefined,
)

export const NotificacoesContextProvider: React.FC<Props> = ({ children }) => {
    const { decoded } = useAuth()
    const navigate = useNavigate()
    const [silentNotifications, setSilentNotifications] = useState<
        StoredNotification[]
    >([])
    const [notifications, setNotifications] = useState<
        (NotificationMessage & { seen: boolean })[]
    >([])
    const [modais, setModais] = useState<NotificationVisualMessage[]>([])
    const cienciasEnviadas = React.useRef<Set<string>>(new Set())
    const unsubscribeRef = React.useRef<() => void>()
    // Notificação de progresso parada (sem nova atualização) por mais que isto
    // é removida automaticamente, evitando barras presas no sino.
    const SILENT_EXPIRY_MS = 10_000
    const expiryTimers = React.useRef<
        Map<string, ReturnType<typeof setTimeout>>
    >(new Map())

    function silentKey(n: StoredNotification) {
        return `${n.module}|${n.type}|${n.ref}|${n.id}`
    }

    function isSameSilent(a: StoredNotification, b: StoredNotification) {
        return (
            a.module === b.module &&
            a.type === b.type &&
            a.ref === b.ref &&
            a.id === b.id
        )
    }

    function clearExpiry(key: string) {
        const timer = expiryTimers.current.get(key)
        if (!timer) return
        clearTimeout(timer)
        expiryTimers.current.delete(key)
    }

    function removeSilent(target: StoredNotification) {
        clearExpiry(silentKey(target))
        setSilentNotifications(current =>
            current.filter(n => !isSameSilent(n, target)),
        )
    }

    function scheduleExpiry(target: StoredNotification) {
        const key = silentKey(target)
        clearExpiry(key)
        expiryTimers.current.set(
            key,
            setTimeout(() => removeSilent(target), SILENT_EXPIRY_MS),
        )
    }

    function updateSilent(value: NotificationSilentMessage) {
        if (typeof value.body !== 'object' || value.body === null) return

        const body = value.body as {
            value?: number | string
            ref?: string
            id?: string
        }

        const numericValue = Number(body.value)
        if (!Number.isFinite(numericValue)) return

        const ref = body.ref ?? value.type
        const id = body.id ?? value.id
        if (!ref || !id) return

        const transformed = {
            value: numericValue,
            module: value.module,
            type: value.type,
            ref,
            id,
        } satisfies StoredNotification

        if (transformed.value >= 100) {
            removeSilent(transformed)
            return
        }

        // Cada atualização reinicia a contagem de 5s.
        scheduleExpiry(transformed)

        setSilentNotifications(current => {
            const exists = current.some(n => isSameSilent(n, transformed))
            if (exists) {
                return current.map(n =>
                    isSameSilent(n, transformed)
                        ? { ...n, value: transformed.value }
                        : n,
                )
            }

            return [...current, transformed]
        })
    }

    function addNotification(message: NotificationMessage) {
        return setNotifications(prev =>
            prev.some(n => n.id === message.id)
                ? prev
                : [...prev, { ...message, seen: false }].sort(
                      (a, b) =>
                          new Date(b.createdAt || '').getTime() -
                          new Date(a.createdAt || '').getTime(),
                  ),
        )
    }

    function markAsSeen(id: string | '*') {
        const alvos = notifications.filter(
            notification =>
                notification.mode !== 'silent' &&
                (id === '*' || notification.id === id),
        ) as NotificationVisualMessage[]

        alvos
            .filter(
                notification =>
                    notification.campanhaId &&
                    modoCiencia(notification.mode) === 'badge',
            )
            .forEach(notification => darCiencia(notification.campanhaId!))

        if (alvos.some(notification => !notification.campanhaId)) {
            notificacoes_api.post('offset', {} as any)
        }

        setNotifications(prev => {
            if (id === '*') {
                return prev.map(n => ({ ...n, seen: true }))
            }

            return prev.map(n => (n.id === id ? { ...n, seen: true } : n))
        })
    }

    /**
     * A ciência é idempotente no servidor (SADD), mas o mesmo id chega por
     * vários caminhos — modal, toast, "marcar todas como lidas" — então o
     * conjunto evita repetir o POST. Em falha o id sai do conjunto para
     * tentar de novo na próxima reconexão, senão a campanha voltaria para
     * sempre sem ninguém registrar a leitura.
     */
    function darCiencia(campanhaId: string) {
        if (cienciasEnviadas.current.has(campanhaId)) return
        cienciasEnviadas.current.add(campanhaId)

        campanhas_ciencia
            .post(`${campanhaId}/ciencia`, {} as never)
            .catch(() => {
                cienciasEnviadas.current.delete(campanhaId)
                toast.error('Não foi possível registrar a leitura do aviso')
            })
    }

    function encerrarModal(notificacao: NotificationVisualMessage) {
        setModais(atual => atual.filter(item => item.id !== notificacao.id))
        if (notificacao.campanhaId) darCiencia(notificacao.campanhaId)
    }

    const subscribe = useTransmit<NotificationMessage>({
        onMessage: payload => {
            if (payload.mode === 'silent') {
                if (
                    payload.module === 'compra' &&
                    payload.type === 'redirect' &&
                    typeof payload.body === 'object' &&
                    payload.body !== null
                ) {
                    const url = (payload.body as { url?: string }).url
                    if (url) {
                        notificacoes_api.post('offset', {} as any)
                        navigate(url)
                    }
                    return
                }
                if (
                    payload.module === 'auth' &&
                    payload.type === 'token-revalidate'
                ) {
                    tokenRefresher.refreshToken()
                    return
                }
                if (payload.type === 'cache-update') {
                    invalidarCacheDoModulo(payload.module)
                    return
                }

                updateSilent(payload)
                return
            }

            for (const mode of payload.mode) {
                switch (mode) {
                    case 'toast':
                        toast[payload.severity || 'info'](payload.message, {
                            id: payload.id,
                            description: payload.title,
                            actionButtonStyle: {
                                background: 'transparent',
                                color: 'inherit',
                                padding: 0,
                            },
                            action: payload.onClickUrl
                                ? {
                                      label: (
                                          <div
                                              title={payload.onClickUrl}
                                              className="flex items-center gap-1"
                                          >
                                              <ExternalLinkIcon className="size-4" />
                                          </div>
                                      ),
                                      type: 'div',
                                      onClick: () => {
                                          payload.onClickUrl!.startsWith('http')
                                              ? window.open(
                                                    payload.onClickUrl,
                                                    '_blank',
                                                )
                                              : navigate(payload.onClickUrl!)
                                      },
                                  }
                                : undefined,
                        })
                        if (
                            payload.campanhaId &&
                            modoCiencia(payload.mode) === 'toast'
                        ) {
                            darCiencia(payload.campanhaId)
                        }
                        break
                    case 'modal':
                        setModais(atual =>
                            atual.some(item => item.id === payload.id)
                                ? atual
                                : [...atual, payload],
                        )
                        break
                    case 'badge':
                        addNotification(payload)
                        break
                    default:
                        throw new Error(
                            `Invalid notification mode: ${
                                mode satisfies never
                            }`,
                        )
                }
            }
        },
    })

    class SubscribeSingleton {
        private static instance: SubscribeSingleton
        public isSubscribedTo: string | null = null
        private isSubscribing = false
        private promise: any = null

        private constructor() {
            // Empty
        }

        public static getInstance(): SubscribeSingleton {
            if (!SubscribeSingleton.instance) {
                SubscribeSingleton.instance = new SubscribeSingleton()
            }
            return SubscribeSingleton.instance
        }

        public async subscribe(channel: string) {
            if (this.isSubscribing) return this.promise
            this.isSubscribing = true
            if (this.isSubscribedTo === channel) {
                return this.promise
            }

            return subscribe(channel)
                .then(unsubscribe => {
                    this.promise = unsubscribe
                })
                .finally(() => {
                    this.isSubscribing = false
                    this.isSubscribedTo = channel
                    notificacoes_api.missed.get()
                })
        }
    }

    const value: NotificacoesContextProps | undefined = !decoded.sub
        ? undefined
        : {
              notifications,
              silentNotifications,
              markAsSeen,
              isReady:
                  !!decoded.sub &&
                  !!SubscribeSingleton.getInstance().isSubscribedTo,
          }

    const channel = decoded.sub ? `notificacao/user/${decoded.sub}` : null

    React.useEffect(() => {
        // Troca de usuário: modal e ciências do anterior não valem para o novo.
        setModais([])
        cienciasEnviadas.current.clear()

        const subscribeToNotifications = async () => {
            if (!channel) return

            unsubscribeRef.current =
                await SubscribeSingleton.getInstance().subscribe(channel)
        }
        subscribeToNotifications()

        return () => {
            unsubscribeRef.current?.()
        }
    }, [channel])

    React.useEffect(() => {
        const timers = expiryTimers.current
        return () => {
            timers.forEach(clearTimeout)
            timers.clear()
        }
    }, [])

    return (
        <NotificacoesContext.Provider value={value}>
            {children}
            {/* Fila: um modal por vez, o próximo aparece ao fechar o atual. */}
            {modais.slice(0, 1).map(notificacao => (
                <ModalCampanha
                    key={notificacao.id}
                    notificacao={notificacao}
                    onEncerrar={() => encerrarModal(notificacao)}
                />
            ))}
        </NotificacoesContext.Provider>
    )
}
